import { useEffect, useMemo, useState } from "react";
import useAppStore from "store/store";
import PageUnknown from "./unknown";
import PageCard from "./card";
import AgeVerification from "modules/AgeVerification/AgeVerification";
import Loading from "modules/Loading/Loading";
import SessionProvider from "modules/Session/SessionProvider";
import PageWelcome from "pages/welcome";

const PageIndex = () => {

    const serverUrl = useAppStore((state) => state.serverUrl);
    const sessionId = useAppStore((state) => state.sessionId);
    const setSessionId = useAppStore((state) => state.setSessionId);
    const setIsPreview = useAppStore((state) => state.setIsPreview);

    const [ customerGuid, setCustomerGuid ] = useState<string | null>( null );
	const [ error, setError ] = useState( false );
    const [ html, setHtml ] = useState('');
    const [ htmlData, setHtmlData ] = useState( {} );
    const [ ready, setReady ] = useState( false );

    // Get the path name!
    const pathname = window.location.pathname

    const sessionKey = useMemo( () => {
        if( !customerGuid ) {
            return null;
        }

        return `sessionId|${customerGuid}`
    }, [ customerGuid ]);

    useEffect( () => {
        // Remove the leading slash from the pathname
        const pathWithoutSlash = pathname.startsWith('/') ? pathname.slice(1) : pathname;

        setCustomerGuid( pathWithoutSlash || null );
    }, [ pathname ]);

    useEffect(() => {
         // If no customer GUID is set, we cannot fetch the data
        if( sessionKey === null ) {
            return
        }
        
        // Check if there already is a session ID in local storage
        const sessionId = localStorage.getItem( sessionKey );
		if ( sessionId ) {
			// Set the session ID in the store
			setSessionId(sessionId);
		}

        // We done
        setReady( true );
    }, [ sessionKey ] )

    useEffect(() => {
        // Reset error state
        setError( false );

        // If no customer GUID is set, we cannot fetch the data
        if( customerGuid === null || sessionKey === null || !ready ) {
            return
        }

        const fetchHeaders = {
            'Session-Id': sessionId
        }

        // Fetch the HTML data from the server
        fetch( `${serverUrl}/api/guest/${customerGuid}`, { headers: fetchHeaders } )
			.then((res) => res.json())
			.then( (data) => {
				if( data ) {

                    if( data.message === 'card_not_found' ) {
                        setError( true );
                        return;
                    }

					setHtml( data.htmlString || '' );	
					setHtmlData( data.htmlData || {} );
                    setIsPreview( data.isPreview === true );

                    // Set the session ID if available
                    setSessionId( data.session || null );
                    if( data.session ) {
                        // Store the session ID in local storage
                        localStorage.setItem( sessionKey, data.session);
                    }
				}
			})
            .catch((error) => {
                console.warn('Error fetching HTML data:', error);
                setError( true );
            });
    }, [ serverUrl, customerGuid, sessionKey, ready ]);

    useEffect(() => {
        // If the HTML data is not available, we cannot apply the background gradient
        if( !htmlData?.config?.guest_background_gradient ) {
            return;
        }

        // Apply the background gradient to the body
        document.documentElement.style.setProperty( '--background-gradient', htmlData.config.guest_background_gradient );
    }, [ htmlData?.config ] )

    // Without a guid we cannot do anything
    // If the guid is null, we show the welcome page
    if( customerGuid === null ) {
        return <PageWelcome />;
    }

    // If there is an error, we show the unknown page
    if( error ) {
        return <PageUnknown />;
    }

    // If the HTML or HTML data is not available, we show a loading spinner
    // This can happen if the fetch request is still in progress and there is no error
    if( !html || !htmlData ) {
        return <Loading />
    }

    return (
        <SessionProvider>
            <AgeVerification
                minAge={ htmlData?.config?.age_minimum || null }>

                <PageCard
                    html={html}
                    htmlData={htmlData}
                />
            </AgeVerification>
        </SessionProvider>
    )
}

export default PageIndex;